import { Grid, InputAdornment, TextField, Typography, useTheme } from '@mui/material';
import React from 'react'
import ScrollableChips from './ScrollableChips';
import ObjectFilterDropdown from './ObjectFilterDropdown';
import moment from 'moment';
import { DraftGroup, Leagues, Pick6MaxLegs } from './store/types';
import { addOptimizerData, clearMatchupsFilter, setIsEntryView, setLeagueFilter, setMaxLegs, setOfferFilters, setSelectedDraftGroup, setWager } from './store/slice';
import { useDispatch, useSelector } from 'react-redux';
import { IRootState } from '../../store/reducer';

const OptimizerHeader = () => {
  const { filters, wager, operator } = useSelector((state: IRootState) => state.pick6);
  const { optimizerData, selectedDraftGroup } = useSelector((state: IRootState) => state.pick6.optimizerState);

  const dispatch = useDispatch();
  const theme = useTheme();
  const [oddsTime, setOddsTime] = React.useState('');
  //These are intermediary states used to prevent hydration errors. Both the options and filters in this case contain time information.
  //Time information is displayed in the locale of the user so for everyone who isn't in the server location the times would be rendered wrong server side.
  const [dropdownOptions, setDropdownOptions] = React.useState<DraftGroup[]>([]);
  const [dropdownFilters, setDropdownFilters] = React.useState<DraftGroup[]>([]);

  React.useEffect(() => {
    // This function is a wrapper due to the async calls for fetching data. useEffect can't be made async.
    const fetchOptimizerData = async () => {
      let leagueFilter = filters?.league;
      if (!leagueFilter) {
        return;
      }
      let leagueCode = Leagues[leagueFilter.toUpperCase() as keyof typeof Leagues];
      if (optimizerData.filter(x => x.leagueCode === leagueFilter)?.length === 0) {
        //Get optimizer data for selected league
        const url = `https://oddsshopper-ui.vercel.app/api/optimizer/data?league=${leagueFilter}&operator=${operator?.toLowerCase()}`;
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          console.log('response', response)
          const data = await response?.json();
          dispatch(addOptimizerData(data));
        } else {
          console.error('Failed to fetch optimizer data');
        }
      } else {
        let defaultedDraftGroup = optimizerData.filter(x => x.leagueCode === leagueCode)[0]?.draftGroups[0] ?? undefined;
        setDropdownOptions(optimizerData.filter(x => x.leagueCode === filters.league)[0]?.draftGroups);
        dispatch(setSelectedDraftGroup(defaultedDraftGroup));
      }
    };
    fetchOptimizerData();
    //Whenever the league changes the filters and dropdowns all need to be updated with league specific information.
    dispatch(clearMatchupsFilter());
    dispatch(setOfferFilters([]));
  }, [filters, dispatch]);
  
  const SetDraftGroup = async (filter: DraftGroup) => {
    dispatch(setSelectedDraftGroup(filter));
  }

  React.useEffect(() => {
    if (filters.league === Leagues.MLB && selectedDraftGroup?.competitionIds?.length < 2) {
      dispatch(setMaxLegs(Pick6MaxLegs.MLB_SINGLE_GAME_SLATE));
    } else {
      dispatch(setMaxLegs(Pick6MaxLegs.DEFAULT))
    }

  }, [filters, selectedDraftGroup, dispatch])

  React.useEffect(() => {
    setDropdownFilters([selectedDraftGroup]);
  }, [selectedDraftGroup, dispatch]);

  React.useEffect(() => {
    const date = new Date();
    setOddsTime(date.toLocaleString([], { hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short' }));
    let leagueFilter = filters?.league;
    let leagueCode = Leagues[leagueFilter?.toUpperCase() as keyof typeof Leagues];
    let defaultedDraftGroup = optimizerData.filter(x => x.leagueCode === leagueCode)[0]?.draftGroups[0] ?? undefined;
    dispatch(setSelectedDraftGroup(defaultedDraftGroup));
    setDropdownOptions(optimizerData.filter(x => x.leagueCode === filters.league)[0]?.draftGroups);
  }, [optimizerData, dispatch])

  const SetLeague = async (filter: string[]) => {
    let leagueFilter = filter.length? filter[0] : '';
    let leagueCode = Leagues[leagueFilter.toUpperCase() as keyof typeof Leagues];
    if (leagueCode && filters.league !== leagueCode) {
      // When changing the league set the lineup selector to show all entries. 
      // This prevents a single lineup from being selected and changing the league again out from under the user.
      // It also allows them to directly generate a new lineup for the league without navigating out of the previous lineup
      dispatch(setIsEntryView(false));
      dispatch(setLeagueFilter(leagueCode));
    }
  }

  const GetOptionLabel = (option: DraftGroup)=> {
    const date = moment(option.minStartTime).local();
    const isToday = date.isSame(moment(), 'day');
    const formattedDateString = isToday ? `Today, ${date.format('h:mm A')}` : date.format('dddd, h:mm A');
    return `${formattedDateString}${option.startTimeSuffix ?? ''}`;
  };

  const GetDefaultLabel = (filters: any[], defaultLabel: string) => {
    if (filters?.[0]) {
      const date = moment(filters[0].minStartTime).local();
      const isToday = date.isSame(moment(), 'day');
      const formattedDateString = isToday ? `Today, ${date.format('h:mm A')}` : date.format('dddd, h:mm A');
      return `${formattedDateString}${filters[0].startTimeSuffix ?? ''}`;
    }
    return defaultLabel;
  };

  return (
    <Grid container sx={{ padding: '20px 10px', width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', backgroundColor: '#101010', }}>
      <Grid xs={12} md={9} container spacing={1.5} item sx={{ width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'start' }}>
        <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
          <img style={{ height: '40px' }} src={`https://awesemostorage.blob.core.windows.net/assets/sportsbooks/light/pick6_light_full.png`} alt="Logo" />
        </Grid>
        <Grid xs={12} item sx={{ display: 'flex' }}>
          <ScrollableChips singleSelect={true} options={['MLB', 'NFL', 'NBA', 'NHL', 'UFC', 'WNBA', 'NCAAF']} selectedOptions={[filters.league as string]} setSelectedOptions={SetLeague}/>
        </Grid>
        <Grid item xs={6} sm={3} sx={{
          display: 'flex', alignSelf: 'center', '& button': {
            textTransform: 'unset !important'
          }
        }}>
          <ObjectFilterDropdown singleSelect defaultLabelOnOne={true} labelPropertyName='name' options={dropdownOptions} filters={dropdownFilters} SetSingleFilter={SetDraftGroup} defaultLabel='Draft Group' GetDefaultLabel={GetDefaultLabel} GetOptionLabel={GetOptionLabel}/>
        </Grid>
        <Grid sx={{ alignSelf: 'center' }} item xs={4}>
          <TextField
            label="Your Wager"
            placeholder="$0.00"
            sx={{
              '& label': {
                color: 'rgba(255, 255, 255, 0.3)',
              },
              '& .MuiInputAdornment-root': {
                '& .MuiTypography-root': {
                  color: 'rgba(255, 255, 255, 0.3)'
                }
              }
            }}
            InputProps={{
              inputMode: 'numeric',
              startAdornment: <InputAdornment position="start" sx={{ color: 'rgba(255, 255, 255, 0.3)'}}>$</InputAdornment>,
              sx: {
                height: '37px',
                maxWidth: '180px',
                color: theme.palette.common.white,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.3) !important',
                }

              },
            }}
            value={wager}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              dispatch(setWager(
                parseInt(event.target.value, 10) > 0
                  ? parseInt(event.target.value, 10)
                  : 0,
              ));
            }}
          />
        </Grid>
      </Grid>
      <Grid sx={{ display: { xs: "none", sm: "block" } }} item>
        <Typography sx={{ color: 'rgba(255, 255, 255, 0.70)', fontSize: 14 }}>Odds last updated: {oddsTime}</Typography>
      </Grid>
    </Grid>
  )
}

export default OptimizerHeader